import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { AVAILABILITY_STYLES } from "@/data/constants";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [wa, setWa] = useState("919848490057");

  useEffect(() => {
    api.get("/site-config").then(({ data }) => setWa(data.business_whatsapp || "919848490057")).catch(() => {});
  }, []);

  useEffect(() => {
    setP(null); setNotFound(false);
    api.get(`/products/${slug}`).then(({ data }) => {
      setP(data);
      document.title = `${data.name} — Aaha Kanakam Agri Traders`;
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="py-32 text-center max-w-md mx-auto px-4">
        <div className="font-serif text-3xl text-forest mb-4">Product not found</div>
        <Link to="/commodities" className="text-forest font-semibold link-underline">← Back to commodities</Link>
      </div>
    );
  }
  if (!p) return <div className="py-32 text-center text-slateneutral">Loading…</div>;

  const badge = AVAILABILITY_STYLES[p.availability] || "bg-stone-100 text-stone-800 border-stone-300";
  const specs = Object.entries(p.specifications || {});

  return (
    <div data-testid={`product-detail-${p.slug}`}>
      <section className="bg-forest text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Link to="/commodities" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold hover:text-white"><ArrowLeft size={14} /> All Commodities</Link>
        </div>
      </section>
      <section className="pb-20 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 -mt-6">
          <div className="lg:col-span-6">
            <div className="aspect-square bg-white border border-borderSubtle overflow-hidden">
              <img src={p.image_url} alt={p.name} onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1615485500834-bc10199bc727?w=1000&q=80"; }} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className={`inline-block text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 border ${badge}`}>{p.availability}</div>
            <div className="eyebrow mt-4">{p.category}</div>
            <h1 className="font-serif text-4xl md:text-5xl text-forest leading-tight mt-2">{p.name}</h1>
            {p.variety && <div className="mt-3 text-sm text-slateneutral"><span className="uppercase tracking-widest text-[11px] text-forest mr-2">Variety:</span>{p.variety}</div>}
            <p className="mt-6 text-base text-slateneutral leading-relaxed">{p.long_description || p.short_description}</p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              <Link to={`/quotation?product=${encodeURIComponent(p.slug)}`} data-testid="detail-request-quote" className="btn-gold px-6 py-3.5 text-sm font-semibold text-center inline-flex items-center justify-center gap-2">
                Request Quote <ArrowRight size={14} />
              </Link>
              <a href={`https://wa.me/${wa}?text=${encodeURIComponent("Hello Aaha Kanakam Agri Traders, I'd like to enquire about " + p.name)}`} target="_blank" rel="noreferrer" className="px-6 py-3.5 text-sm font-semibold text-center border border-forest text-forest hover:bg-forest hover:text-white transition inline-flex items-center justify-center gap-2">
                <MessageCircle size={14} /> WhatsApp Enquiry
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 mt-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 bg-white border border-borderSubtle p-6 md:p-8">
            <div className="eyebrow mb-4">Typical Specifications</div>
            {specs.length === 0 ? (
              <p className="text-sm text-slateneutral">Specifications available on request based on order volume and buyer requirement.</p>
            ) : (
              <dl className="divide-y divide-borderSubtle">
                {specs.map(([k, v]) => (
                  <div key={k} className="grid grid-cols-2 py-3 text-sm">
                    <dt className="text-charcoal/70 uppercase tracking-widest text-[11px]">{k}</dt>
                    <dd className="text-charcoal font-medium text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            )}
            <p className="mt-6 text-xs text-slateneutral italic border-t border-dashed border-borderSubtle pt-4">
              Specifications, packaging and availability can be customised based on buyer requirements and order volume.
            </p>
          </div>
          <div className="lg:col-span-5 space-y-4">
            <Info label="Origin" value={p.origin} />
            <Info label="Packaging Options" value={p.packaging || "Buyer-specific packaging on request"} />
            <Info label="Minimum Order Quantity" value={p.moq || "Subject to buyer requirement"} />
            <Info label="Supply Capacity" value={p.supply_capacity || "Available on confirmation"} />
            <Info label="Shipment Options" value={p.shipment_options || "FOB / CIF / CFR — configurable"} />
            <Info label="Quality Documentation" value="Certificate of Analysis and phytosanitary documentation coordinated as required" />
          </div>
        </div>
      </section>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="p-5 bg-white border border-borderSubtle">
      <div className="eyebrow mb-1.5">{label}</div>
      <div className="text-sm text-charcoal">{value}</div>
    </div>
  );
}
